import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../../context/GameContext';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiMic, FiWind } = FiIcons;

export default function SELCalmBubbles() {
  const navigate = useNavigate();
  const { dispatch } = useGame();
  const [level, setLevel] = useState(1);
  const [bubbles, setBubbles] = useState([]);
  const [popped, setPopped] = useState(0);
  const [target, setTarget] = useState(5);
  const [completed, setCompleted] = useState(false);
  const [breathing, setBreathing] = useState(false);

  // Generate bubbles
  useEffect(() => {
    const newBubbles = Array.from({ length: target }, (_, i) => ({
      id: i,
      x: Math.random() * 70 + 15, // Keep away from edges
      y: Math.random() * 50 + 20, // Keep away from edges
      size: Math.random() * 40 + 60,
      color: ['bg-blue-300', 'bg-cyan-300', 'bg-teal-300', 'bg-indigo-300'][Math.floor(Math.random() * 4)],
      emotion: ['angry', 'frustrated', 'worried', 'sad'][Math.floor(Math.random() * 4)]
    }));
    setBubbles(newBubbles);
  }, [level, target]);

  const popBubble = (id) => {
    setBubbles(prev => prev.filter(b => b.id !== id));
    setPopped(p => p + 1);
    
    if (popped + 1 >= target) {
      if (level < 3) {
        setLevel(l => l + 1);
        setTarget(t => t + 3);
        setPopped(0);
        dispatch({ type: 'ADD_SCORE', payload: 20 });
      } else {
        setCompleted(true);
        dispatch({ type: 'GAME_COMPLETED', payload: { questId: 'sel-calm-bubbles', bonusTokens: 25 } });
      }
    }
  };

  const startBreathing = () => {
    setBreathing(true);
    // Simulate breathing by automatically popping a bubble slowly
    if (bubbles.length > 0) {
      popBubble(bubbles[0].id);
    }
    setTimeout(() => setBreathing(false), 1500);
  };

  if (completed) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-cyan-100 to-blue-100">
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-cyan-200 text-center max-w-md">
          <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 3, repeat: Infinity }} className="text-6xl mb-4">🫧</motion.div>
          <h2 className="text-3xl font-kid text-cyan-700 mb-4">Calm Master!</h2>
          <p className="font-comic text-cyan-600 mb-6">You helped everyone feel peaceful! Remember to take deep breaths when you feel upset.</p>
          <button onClick={() => navigate('/dashboard')} className="w-full bg-cyan-500 hover:bg-cyan-600 text-white p-3 rounded-2xl font-kid transition-colors">Back to Dashboard</button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen p-4 pb-24 bg-gradient-to-br from-cyan-50 to-blue-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 bg-white rounded-2xl px-4 py-2 shadow border-2 border-cyan-200">
            <SafeIcon icon={FiArrowLeft} /> Back
          </button>
          <div className="bg-white rounded-2xl px-4 py-2 shadow border-2 border-cyan-200">
            <span className="font-kid text-cyan-700">Level {level}</span>
          </div>
          <div className="bg-cyan-100 rounded-2xl px-4 py-2 shadow border-2 border-cyan-300">
            <span className="font-kid text-cyan-700">{popped}/{target} 🫧</span>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-2xl p-4 mb-6 text-center border-2 border-cyan-100">
          <p className="font-comic text-cyan-700">
            {level === 1 ? "Tap bubbles slowly to pop them, or use the breathe button!" : 
             level === 2 ? "Pop the 'angry' and 'frustrated' bubbles first to calm down!" : 
             "Help the character calm down by popping all the emotional bubbles!"}
          </p>
        </div>

        {/* Breathing Button */}
        <div className="text-center mb-6 z-10 relative">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={startBreathing}
            disabled={breathing}
            className={`text-white px-6 py-3 rounded-2xl font-kid flex items-center gap-2 mx-auto shadow-lg transition-colors ${breathing ? 'bg-cyan-300' : 'bg-cyan-500 hover:bg-cyan-600'}`}
          >
            <SafeIcon icon={breathing ? FiWind : FiMic} /> 
            {breathing ? 'Breathe out...' : 'Take a Deep Breath'}
          </motion.button>
        </div>

        {/* Bubble Arena */}
        <div className="relative h-[55vh] bg-white/50 rounded-3xl border-4 border-cyan-200 overflow-hidden shadow-inner">
           <AnimatePresence>
              {bubbles.map(bubble => (
                <motion.button
                  key={bubble.id}
                  initial={{ scale: 0 }}
                  animate={{ 
                    scale: 1, 
                    y: [0, -15, 0],
                    x: [0, 10, -10, 0]
                  }}
                  transition={{ 
                    y: { duration: 3 + Math.random(), repeat: Infinity, ease: 'easeInOut' },
                    x: { duration: 4 + Math.random(), repeat: Infinity, ease: 'easeInOut' }
                  }}
                  exit={{ scale: 1.5, opacity: 0, transition: { duration: 0.2 } }}
                  onClick={() => popBubble(bubble.id)}
                  className={`absolute rounded-full border-2 border-white/50 shadow-lg flex items-center justify-center ${bubble.color} backdrop-blur-sm cursor-pointer hover:brightness-110`}
                  style={{
                    left: `${bubble.x}%`,
                    top: `${bubble.y}%`,
                    width: bubble.size,
                    height: bubble.size,
                  }}
                >
                  <div className="w-[30%] h-[30%] bg-white/40 rounded-full absolute top-[20%] left-[20%]" />
                  {level >= 2 && (
                    <span className="text-white font-comic text-xs drop-shadow-md px-1 text-center leading-tight">
                      {bubble.emotion}
                    </span>
                  )}
                </motion.button>
              ))}
            </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}